import React, { useState } from 'react';
import { variant, match, fields } from 'variant';
import type { VariantOf } from 'variant';
import { getRepoType, createPackageManager } from '@wix/package-manager';
import { Box, ConfirmInput, Spinner, Text } from '@wix/cli-ui-kit';
import { useProjectModel } from '../../model.js';

interface Props {
  onFinish: () => void;
  onSubmit?: () => void;
}

const InstallationStateVariant = variant({
  NotStarted: {},
  Skipped: fields<{ pkgManagerName: string }>(),
  Installing: {},
  Failure: {},
  Success: {},
});

type InstallationState = VariantOf<typeof InstallationStateVariant>;

export const InstallDependencies: React.FC<Props> = ({
  onFinish,
  onSubmit,
}) => {
  const { model } = useProjectModel();

  const [installState, setInstallState] = useState<InstallationState>(
    InstallationStateVariant.NotStarted()
  );

  return (
    <>
      <Box marginBottom={1}>
        <ConfirmInput
          label={'Would you like to install dependencies now?'}
          initialValue={true}
          onSubmit={async (confirmed) => {
            const { projectFolder } = model;
            const repoType = await getRepoType(projectFolder);
            const pkgManager = await createPackageManager(repoType);

            onSubmit?.();

            if (confirmed) {
              setInstallState(InstallationStateVariant.Installing());
              try {
                await pkgManager.runInstall(projectFolder);
              } catch (e) {
                setInstallState(InstallationStateVariant.Failure());
                throw e;
              }
              setInstallState(InstallationStateVariant.Success());
            } else {
              setInstallState(
                InstallationStateVariant.Skipped({
                  pkgManagerName: pkgManager.name,
                })
              );
            }

            onFinish();
          }}
        />
      </Box>
      {match(installState, {
        NotStarted: () => null,
        Failure: () => null,
        Skipped: ({ pkgManagerName }) => (
          <Box flexDirection="column">
            <Text skin="warning">
              ⚠️ New app dependencies have been added. Please run{' '}
              <Text skin="info">{pkgManagerName} install</Text> before
              continuing to work.
            </Text>
          </Box>
        ),
        Installing: () => <Spinner text={'Loading...'} />,
        Success: () => (
          <Box flexDirection="column">
            <Text>✅ Dependencies installed</Text>
          </Box>
        ),
      })}
    </>
  );
};
